import { addDays } from '@painpal/core';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatHoursMinutes, formatWhole } from '../../lib/format';
import { useIsPhone } from '../../ui/useMediaQuery';
import { weekRangeLabel } from '../fitness/weekSnapshotModel';
import { formatEstimate, weekOffsetLabel, type WeekPlanTotals } from './planModel';
import styles from './calendar.module.css';

export interface CalendarWeekHeaderProps { week: string; thisWeek: string; totals: WeekPlanTotals | undefined }

// Links, not buttons: the week lives in the URL, so back and forward page with it.
function Arrow({ week, direction }: { week: string; direction: 'prev' | 'next' }) {
  const prev = direction === 'prev';
  const Icon = prev ? ChevronLeft : ChevronRight;
  return (
    <Link to="/calendar" search={{ week: addDays(week, prev ? -7 : 7) }} className={styles.arrow} aria-label={prev ? 'Previous week' : 'Next week'}>
      <span className={styles.arrowBox}><Icon size={14} aria-hidden="true" /></span>
    </Link>
  );
}

// Planned is quiet and ridden is ink. An empty week shows only its (zero) plan.
function Totals({ totals }: { totals: WeekPlanTotals | undefined }) {
  if (totals === undefined) return null;
  return (
    <div className={styles.totals}>
      <p className={styles.total} data-tone="planned">
        <span className={styles.totalLabel}>PLANNED</span>{' '}
        <span className={styles.totalValue}><span className={styles.figure}>{formatHoursMinutes(totals.plannedS)}</span> · <span className={styles.figure}>{formatEstimate(totals.plannedLoad)}</span> LOAD</span>
      </p>
      {totals.blocks > 0 && (
        <>
          <span className={styles.rule} aria-hidden="true" />
          <p className={styles.total} data-tone="ridden">
            <span className={styles.totalLabel}>RIDDEN</span>{' '}
            <span className={styles.totalValue}><span className={styles.figure}>{formatHoursMinutes(totals.riddenS)}</span> · <span className={styles.figure}>{formatWhole(totals.riddenLoad)}</span> LOAD</span>
          </p>
        </>
      )}
    </div>
  );
}

function DesktopHeader({ week, thisWeek, totals }: CalendarWeekHeaderProps) {
  return (
    <div className={styles.weekHeader}>
      <div className={styles.nav}>
        <Arrow week={week} direction="prev" />
        <h2 className={styles.title}>{weekRangeLabel(week)}</h2>
        <Arrow week={week} direction="next" />
        <span className={styles.eyebrow}>{weekOffsetLabel(week, thisWeek)}</span>
        {week !== thisWeek && <Link to="/calendar" search={{}} className={styles.thisWeek}>THIS WEEK</Link>}
      </div>
      <Totals totals={totals} />
    </div>
  );
}

function PhoneHeader({ week, thisWeek, totals }: CalendarWeekHeaderProps) {
  const offset = weekOffsetLabel(week, thisWeek);
  return (
    <div className={styles.weekHeader}>
      <div className={styles.phoneNav}>
        <Arrow week={week} direction="prev" />
        <div className={styles.dates}>
          <h2 className={styles.title}>{weekRangeLabel(week)}</h2>
          {week === thisWeek
            ? <span className={styles.eyebrow}>{offset}</span>
            : <Link to="/calendar" search={{}} className={styles.eyebrow}>{offset} · TAP TO RETURN</Link>}
        </div>
        <Arrow week={week} direction="next" />
      </div>
      <Totals totals={totals} />
    </div>
  );
}

export function CalendarWeekHeader(props: CalendarWeekHeaderProps) {
  const Header = useIsPhone() ? PhoneHeader : DesktopHeader;
  return <Header {...props} />;
}
